import { useEffect, useRef, useState } from "react";

import * as Yup from "yup";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { Check, ChevronDown, Loader2 } from "lucide-react";

import { cn } from "../../../lib/utils";
import {
  decryptPayload,
  getObjLength,
  getSignupUserId,
  mergeGenerateQuestionsPayload,
  persistPropertySearchAndBillingPrefillFromReport,
  persistSignupMonitoringPropertyIds,
} from "../../helpers/utils";
import {
  canUseParcelSearch,
  getEnhancedMonitoringUnavailableMessage,
} from "../../helpers/enhancedMonitoringSearchRules";
import { Label } from "../../common/customDesignedComponents/label";
import { Button } from "../../common/customDesignedComponents/button";
import { Checkbox } from "../../common/customDesignedComponents/checkbox";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";
import HubspotCookieBreakerInput from "../../common/HubspotCookieBreakerInput";
import PropertySearchErrorMessage from "../PropertySearchErrorMessage";
import { FormInput } from "../../common/customDesignedComponents/FormInput";
import { propertiesAction } from "../../../redux/slices/properties/propertiesSlice";
const validationSchema = Yup.object().shape({
  parcel_number: Yup.string().required("Parcel number is required"),
  county_code: Yup.string()
    .required("County is required")
    .test("not-empty", "County is required", (value) => {
      return value !== undefined && value !== null && value !== "";
    }),
});

interface CountyOption {
  id: string | number;
  county: string;
}

const FindPropertyByParcelV2 = ({
  onSubmit = (values: any, errors: any, checked1: boolean) => {},
  propertyValues,
  errMsg,
  questionsErrMsg,
  setChecked1 = (checked: boolean) => {},
  hasSearched = false,
  onBtnClick = () => {},
  isPropertyConfirmed = null,
  propertyQuestion,
  category,
  agreementCategory,
  isByPassOptionAvailable = false,
  isAdminCreateUser = false,
  isServiceProvider = false,
  isAssistant = false,
  categoryChosenBeforeSearch = false,
}) => {
  const dispatch = useDispatch();
  const { isQuestionByPass } = useSelector(
    (state: any) => state?.properties,
  );
  // SP client add/create (/clients/*): auto-bypass — hide the checkbox.
  // SP personal (/servicer-properties) and Admin still show it.
  const showBypassCheckbox =
    isByPassOptionAvailable &&
    !isAssistant &&
    !(isServiceProvider && isAdminCreateUser);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<CountyOption[]>([]);
  const [countySearchValue, setCountySearchValue] = useState("");
  const [countySearchPending, setCountySearchPending] = useState(false);
  const countyInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const countyDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const COUNTY_SEARCH_DEBOUNCE_MS = 400;

  const {
    countyList,
    statesLoader,
    selectedProperty,
    createAccResponse,
    user_id,
    statesCodeList,
  } = useSelector((state: any) => state.signUpUser);
  const { genearteQuesPayload } = useSelector(
    (state: any) => state?.properties,
  );
  const resolvedCategory =
    agreementCategory ||
    category ||
    decryptPayload(localStorage.getItem("category")) ||
    "complete_protection";
  const parcelSearchAllowed = canUseParcelSearch(resolvedCategory, {
    categoryChosenBeforeSearch,
  });
  const isMonitoringCategory =
    resolvedCategory === "enhanced_monitoring" ||
    resolvedCategory === "basic_monitoring" ||
    category === "enhanced_monitoring" ||
    category === "basic_monitoring";
  const isCompleteProtection = resolvedCategory === "complete_protection";
  const initialValues = {
    parcel_number: propertyValues?.parcel_number || "",
    county_code: propertyValues?.county_code || "",
    county_name: propertyValues?.county_name || "",
  };

  const clearCountyDebounce = () => {
    if (countyDebounceRef.current) {
      clearTimeout(countyDebounceRef.current);
      countyDebounceRef.current = null;
    }
  };

  const getCountyList = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue?: (field: string, value: any) => void,
  ) => {
    const value = event.target.value;
    setCountySearchValue(value);

    // Clear form values if input is cleared
    if (setFieldValue && value.length === 0) {
      setFieldValue("county_code", "");
      setFieldValue("county_name", "");
    }

    clearCountyDebounce();

    if (value.trim().length >= 3) {
      setOpen(true);
      setCountySearchPending(true);
      countyDebounceRef.current = setTimeout(() => {
        countyDebounceRef.current = null;
        dispatch(
          userSigupAction.getCountyList({
            url: "get-county-autocomplete",
            data: { county: value.trim() },
          }),
        );
      }, COUNTY_SEARCH_DEBOUNCE_MS);
    } else {
      setCountySearchPending(false);
      setOptions([]);
      setOpen(false);
    }
  };

  const countyQueryReady = countySearchValue.trim().length >= 3;
  const isCountySearching = countySearchPending || statesLoader;
  const showCountyDropdown =
    open && (countyQueryReady || options.length > 0 || isCountySearching);

  const goToNextStep = () => {
    // First check if terms and conditions are agreed (Complete Protection only)
    if (isCompleteProtection && !agreeToTerms) {
      setTermsError("You must agree to the security terms to continue");
      return;
    }
    setTermsError("");
    if (isMonitoringCategory) {
      // Admin/SP create-client already runs create / add-property on select.
      if (isAdminCreateUser) {
        if (propertyQuestion?.success) {
          handleNextStepAction();
        }
        return;
      }

      if (propertyQuestion?.success) {
        handleNextStepAction();
      }
      return;
    } else {
      handleNextStepAction();
    }
  };
  const handleNextStepAction = () => {
    dispatch(userSigupAction.showSecurityInfoScreen(true));
    dispatch(propertiesAction.showSecurityInfoScreen(true));
    onBtnClick();
    if (getObjLength(propertyQuestion?.data)) {
      persistPropertySearchAndBillingPrefillFromReport(propertyQuestion.data, {
        selectedProperty,
      });
    }

    if (propertyQuestion?.data) {
      persistSignupMonitoringPropertyIds(
        dispatch,
        userSigupAction,
        propertyQuestion.data,
      );
    }

    localStorage.setItem("button", "back");
  };

  useEffect(() => {
    if (
      isMonitoringCategory &&
      getObjLength(propertyQuestion) &&
      propertyQuestion?.success
    ) {
      handleNextStepAction();
    }
  }, [propertyQuestion]);

  useEffect(() => {
    if (getObjLength(countyList)) {
      if (countyList?.success) {
        setOptions([...(countyList?.data || [])]);
      }
    }
  }, [countyList]);

  // Initialize county search value from propertyValues
  useEffect(() => {
    if (propertyValues?.county_name) {
      setCountySearchValue(propertyValues.county_name);
    }
  }, [propertyValues?.county_name]);

  useEffect(() => {
    if (statesLoader) {
      setCountySearchPending(false);
    }
  }, [statesLoader]);

  useEffect(() => {
    if (isCountySearching && countySearchValue.trim().length >= 3) {
      setOpen(true);
    }
  }, [isCountySearching, countySearchValue]);

  useEffect(() => {
    return () => {
      clearCountyDebounce();
    };
  }, []);

  // Close dropdown when clicking outside — keep it open while counties load
  // so the user can wait for results to fill in.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isCountySearching) {
        return;
      }
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        countyInputRef.current &&
        !countyInputRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCountySearching]);

  const handleSubmit = (values: any) => {
    if (!parcelSearchAllowed) {
      return;
    }
    if (isCompleteProtection && !agreeToTerms) {
      setTermsError("You must agree to the security terms to continue");
      return;
    }
    setTermsError("");
    dispatch(userSigupAction.fetchUserPropertyData());
    dispatch(userSigupAction.selectedUserPropertyData({}));
    setChecked1(true);
    onSubmit(values, null, true);
  };

  if (!parcelSearchAllowed) {
    return (
      <div className="mt-2 space-y-3">
        <div className="grid grid-cols-1 gap-3 pointer-events-none sm:grid-cols-2 sm:gap-4">
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">
              Parcel Number
            </Label>
            <input
              type="text"
              disabled
              placeholder="Parcel Number"
              className="h-10 w-full rounded-md border border-input bg-muted/40 px-3 py-2 text-sm cursor-not-allowed opacity-60 shadow-none"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">County</Label>
            <input
              type="text"
              disabled
              placeholder="Search the County"
              className="h-10 w-full rounded-md border border-input bg-muted/40 px-3 py-2 text-sm cursor-not-allowed opacity-60 shadow-none"
            />
          </div>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {getEnhancedMonitoringUnavailableMessage()}
        </p>
      </div>
    );
  }

  return (
    <>
      <Formik
        enableReinitialize
        validateOnChange
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          setFieldValue,
          setFieldTouched,
          touched,
          errors,
          isSubmitting,
          handleSubmit,
          validateField,
          setFieldError,
        }) => {
          return (
            <Form
              onSubmit={handleSubmit}
              id="signup-find-property-by-parcel-form"
            >
              {/* Hubspot hidden input for cookie breaker */}
              <HubspotCookieBreakerInput />

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                <div className="space-y-1">
                  <Label
                    htmlFor="parcel_number"
                    className="text-muted-foreground text-xs"
                  >
                    Parcel Number
                  </Label>
                  <FormInput
                    name="parcel_number"
                    id="parcelNumber"
                    placeholder="Parcel Number"
                    className="h-10 text-sm shadow-none"
                    validation={{
                      values,
                      setFieldValue,
                      setFieldTouched,
                      touched,
                      errors,
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label
                    htmlFor="county_code"
                    className="text-muted-foreground text-xs"
                  >
                    County
                  </Label>
                  <div className="relative overflow-visible">
                    <input
                      ref={countyInputRef}
                      type="text"
                      value={countySearchValue || values.county_name || ""}
                      onChange={(e) => getCountyList(e, setFieldValue)}
                      onFocus={() => {
                        if (countySearchValue.trim().length >= 3) {
                          setOpen(true);
                        }
                      }}
                      placeholder="Search the County"
                      className={cn(
                        "h-10 w-full rounded-md border bg-background px-3 py-2 pr-10 text-sm shadow-none ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                        touched.county_code && Boolean(errors.county_code)
                          ? "border-red-500"
                          : "border-input",
                      )}
                    />
                    <div className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1">
                      {isCountySearching ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>

                    {/* Dropdown List — stay open while counties are loading */}
                    {showCountyDropdown && (
                      <div
                        ref={dropdownRef}
                        className="absolute z-[1300] mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
                      >
                        {isCountySearching && options.length === 0 ? (
                          <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                            Searching counties…
                          </div>
                        ) : null}
                        {isCountySearching && options.length > 0 ? (
                          <div className="flex items-center gap-2 border-b border-border px-3 py-2 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
                            Updating results…
                          </div>
                        ) : null}
                        {!isCountySearching && options.length === 0 ? (
                          <div className="px-3 py-2.5 text-sm text-muted-foreground">
                            No counties found
                          </div>
                        ) : null}
                        {options.map((option) => {
                          const isSelected =
                            String(values.county_code) === String(option.id);
                          return (
                            <div
                              key={option.id}
                              onClick={async () => {
                                const countyCodeValue = String(option.id);
                                await setFieldValue(
                                  "county_code",
                                  countyCodeValue,
                                  false,
                                );
                                await setFieldValue(
                                  "county_name",
                                  option.county,
                                  false,
                                );
                                setFieldTouched("county_code", true, false);
                                setCountySearchValue(option.county);
                                clearCountyDebounce();
                                setCountySearchPending(false);
                                setOpen(false);
                                setFieldError("county_code", undefined);
                                requestAnimationFrame(() => {
                                  validateField("county_code");
                                });
                              }}
                              className={cn(
                                "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                                isSelected && "bg-accent",
                              )}
                            >
                              <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                                {isSelected && <Check className="h-4 w-4" />}
                              </span>
                              <span>{option.county}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {touched.county_code && Boolean(errors.county_code) && (
                      <p className="text-sm text-red-500 mt-1 tailwind_error_msg">
                        {typeof errors.county_code === "string"
                          ? errors.county_code
                          : "County is required"}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Agreement Checkbox — required before search (Complete Protection only) */}
              {isCompleteProtection && (
                <>
                  <div className="flex items-start gap-2 mt-6 mb-6">
                    <Checkbox
                      id="agreement"
                      checked={agreeToTerms}
                      onCheckedChange={(checked) => {
                        setAgreeToTerms(checked as boolean);
                        if (checked) {
                          setTermsError("");
                        }
                      }}
                      className="mt-0.5"
                    />
                    <Label
                      htmlFor="agreement"
                      className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                    >
                      For security reasons, I understand that I will be asked
                      challenge questions and/or my customer service PIN to
                      verify my identity. If I receive a call and I am NOT asked
                      the proper challenge questions, I will hang up and call
                      Customer Support immediately.
                    </Label>
                  </div>
                  {termsError && (
                    <p className="text-xs text-red-500 mb-4 mt-4">
                      {termsError}
                    </p>
                  )}
                </>
              )}

              {showBypassCheckbox ? (
                <div className="flex items-start gap-2 mb-6">
                  <Checkbox
                    id="bypass-security-questions-parcel"
                    checked={Boolean(isQuestionByPass)}
                    onCheckedChange={(checked) => {
                      dispatch(
                        propertiesAction.setIsQuestionByPass(checked === true),
                      );
                    }}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor="bypass-security-questions-parcel"
                    className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
                  >
                    Do you want to bypass security questions for this property?
                    {isAdminCreateUser
                      ? " Use this when ownership was verified another way."
                      : ""}
                  </Label>
                </div>
              ) : null}

              {(errMsg || questionsErrMsg) && (
                <PropertySearchErrorMessage
                  message={errMsg || questionsErrMsg}
                  className="text-center text-red-500 text-sm my-4"
                  linkClassName="text-secondary hover:underline font-medium"
                />
              )}

              {/* Next Step Button */}
              <div
                className={`flex justify-center mt-6 ${propertyQuestion?.success || (isMonitoringCategory && isPropertyConfirmed) ? "gap-4" : ""}`}
              >
                <Button
                  type="submit"
                  // disabled={isSubmitting}
                  className={`font-medium px-10 py-2.5 text-sm rounded-full shadow-md transition-all
                      bg-secondary bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 
                      hover:to-cyan-400/90 text-white hover:shadow-lg ring-2 ring-secondary/30 ring-offset-2`}
                >
                  SEARCH {hasSearched ? "AGAIN" : ""}
                </Button>
                {(propertyQuestion?.success ||
                  (isMonitoringCategory && isPropertyConfirmed)) && (
                  <Button
                    type="button"
                    disabled={!isPropertyConfirmed}
                    onClick={goToNextStep}
                    className={`font-medium px-10 py-2.5 text-sm rounded-full shadow-md transition-all
                      bg-secondary bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 
                      hover:to-cyan-400/90 text-white hover:shadow-lg ring-2 ring-secondary/30 ring-offset-2`}
                  >
                    NEXT
                  </Button>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default FindPropertyByParcelV2;
